import React, { Fragment } from 'react';
import styled from 'styled-components';
import { Steps } from 'antd';
import { colors } from 'helpers/ColorHelper';
import { media } from 'helpers/MediaHelper';
import PillButton from 'components/ui/Buttons/PillButton';
import TextButton from 'components/ui/Buttons/TextButton';
import Loading from 'components/ui/Animations/Loading';

import 'antd/lib/steps/style/index.css';

const { Step: AntdStep } = Steps;

const StyledStep = styled(AntdStep)`
  // Checkmark icons were misaligned without this
  .anticon svg {
    display: flex;
  }

  // Checkmark icons were misaligned without this
  .anticon {
    display: inline-block;
    color: inherit;
    font-style: normal;
    line-height: 0;
    text-align: center;
    text-transform: none;
    vertical-align: -0.125em;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .ant-steps-icon {
    color: ${colors.WHITE} !important;
  }

  .ant-steps-item-icon {
    background: ${props => (props.disabled ? colors.OFF_WHITE : colors.ULTRAMARINE_BLUE)};
    border-color: ${props => (props.disabled ? colors.OFF_WHITE : colors.ULTRAMARINE_BLUE)};
    color: ${colors.WHITE};
  }

  // Grey line was appearing below the last bullet
  .ant-steps-item-tail {
    padding: 26px 0 6px !important;
  }

  .ant-steps-item-tail::after {
    background-color: ${colors.OFF_WHITE};
  }
`;

const StyledStepTitle = styled.p`
  margin: 0;
  font-weight: 500;
  font-size: 16px;
  color: ${props => (props.disabled ? colors.CHAR_COUNT : colors.SPACE_GRAY)};
`;

const StyledStepDescription = styled.p`
  margin-top: 8px;
  font-size: 14px;
  color: ${props => (props.disabled ? colors.CHAR_COUNT : colors.MED_DARK_GRAY)};
`;

const StyledButtonContainer = styled.div`
  display: flex;
  margin-top: 24px;
  margin-bottom: 24px;

  ${media.mobile`
    flex-direction: column;
    align-items: space-between;
  `};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTextWithLoading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Stepper = ({ currentStep, onChange, steps }) => {
  return (
    <Steps direction="vertical" size="small" onChange={onChange} current={currentStep}>
      {steps.map((step, index) => {
        const {
          title,
          description,
          primaryAction,
          primaryActionText,
          secondaryAction,
          secondaryActionText,
          isLoading,
          primaryActionIsLoading,
          loadingDescription,
        } = step;

        return (
          <StyledStep
            disabled={index > currentStep}
            title={<StyledStepTitle disabled={index > currentStep}>{title}</StyledStepTitle>}
            description={
              index === currentStep &&
              (isLoading ? (
                <LoadingContainer>
                  <Loading width={25} height={25} style={{ margin: '-5px 8px 0 0' }} />
                  {<StyledStepDescription>{loadingDescription}</StyledStepDescription>}
                </LoadingContainer>
              ) : (
                <Fragment>
                  {<StyledStepDescription>{description}</StyledStepDescription>}
                  {primaryAction && (
                    <StyledButtonContainer>
                      {primaryAction && (
                        <PillButton onClick={primaryAction} disabled={primaryActionIsLoading}>
                          {primaryActionIsLoading ? (
                            <TextWithLoading text={primaryActionText} />
                          ) : (
                            primaryActionText
                          )}
                        </PillButton>
                      )}
                      {secondaryAction && (
                        <TextButton onClick={secondaryAction}>{secondaryActionText}</TextButton>
                      )}
                    </StyledButtonContainer>
                  )}
                </Fragment>
              ))
            }
          />
        );
      })}
    </Steps>
  );
};

const TextWithLoading = ({ text }) => {
  return (
    <StyledTextWithLoading>
      {text}
      <Loading height={30} width={30} />
    </StyledTextWithLoading>
  );
};

export default Stepper;
